/**
 * Admin Ticket View Page - Full-page ticket image viewer with actions.
 *
 * Native-like design inspired by iOS Photos / Android Gallery:
 * - Full-screen image with tap to toggle toolbar
 * - Bottom toolbar with actions (always visible or toggleable)
 * - Swipe left/right to navigate between tickets
 * - Smooth animations and haptic feedback
 * - Regeneration modes: imageOnly (keep number) or full (new number)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { z } from "zod";
import {
  IconAlertTriangle,
  IconChevronLeft,
  IconChevronRight,
} from "../../../components/Icons.tsx";
import { Loader } from "../../../components/Loader.tsx";
import { RegenerateSheet } from "../orders/components/RegenerateSheet.tsx";
import { useSafeTimeout } from "../../../lib/hooks/use-safe-timeout.ts";
import { SendSheet } from "../orders/components/SendSheet.tsx";
import { getTelegram, haptic, safeShowAlert } from "../../../lib/telegram.ts";
import { getAuthHeaders, getErrorMessage } from "../../../lib/graphql.ts";
import { config } from "../../../config.ts";
import { adminLog } from "../../../lib/logger.ts";
import {
  useResendTicket,
  useResendTickets,
  useRegenerateTicket,
  useRegenerateTickets,
} from "./hooks";

// Session storage keys for state restoration
const SCROLL_STORAGE_KEY = "admin_ticket_view_scroll";
const EXPANDED_ORDER_STORAGE_KEY = "admin_ticket_view_expanded_order";

type ImageOrientation = "portrait" | "landscape" | "square" | null;
type RegenerateTarget = "single" | "all";

/**
 * Runtime validation schema for location.state.
 * 
 * React Router's location.state can be any value (it's untyped).
 * Using Zod ensures we handle malformed state gracefully instead of
 * crashing on undefined property access.
 */
const LocationStateSchema = z.object({
  from: z.string().optional(),
  ticketIds: z.array(z.string()).optional(),
  orderId: z.string().optional(),
  scrollY: z.number().optional(),
});

// Icons for actions
function IconSend({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 2L11 13" />
      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
    </svg>
  );
}

function IconRefreshCw({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 4V10H17" />
      <path d="M1 20V14H7" />
      <path d="M3.51 9A9 9 0 0114.85 3.15L21 10M3 14L9.15 20.85A9 9 0 0020.49 15" />
    </svg>
  );
}

function IconMoreVertical({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

export function AdminTicketViewPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const tg = getTelegram();
  const { setTimeout: safeSetTimeout } = useSafeTimeout();

  // URQL mutation hooks
  const { resendTicket, isLoading: isResendingTicket } = useResendTicket();
  const { resendTickets, isLoading: isResendingTickets } = useResendTickets();
  const { regenerateTicket, isLoading: isRegeneratingTicket } = useRegenerateTicket();
  const { regenerateTickets, isLoading: isRegeneratingTickets } = useRegenerateTickets();

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orientation, setOrientation] = useState<ImageOrientation>(null);
  const [showToolbar, setShowToolbar] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  // Regeneration state
  const [showRegenerateSheet, setShowRegenerateSheet] = useState(false);
  const [regenerateTarget, setRegenerateTarget] =
    useState<RegenerateTarget>("single");

  // Send sheet state
  const [showSendSheet, setShowSendSheet] = useState(false);
  const [sendTarget, setSendTarget] = useState<"single" | "all">("single");
  
  // Derived loading states
  const isProcessing = isResendingTicket || isResendingTickets;
  const isRegenerating = isRegeneratingTicket || isRegeneratingTickets;

  const objectUrlRef = useRef<string | null>(null);

  // Get navigation state with runtime validation
  // SECURITY: location.state can be any value - validate before use
  const validatedState = useMemo(() => {
    const result = LocationStateSchema.safeParse(location.state);
    return result.success ? result.data : null;
  }, [location.state]);

  const returnPath = validatedState?.from || "/admin/orders";
  const ticketIds = validatedState?.ticketIds || [];
  const orderId = validatedState?.orderId;
  const savedScrollY = validatedState?.scrollY ?? 0;

  // Current ticket index for navigation
  const currentIndex = ticketIds.indexOf(ticketId || "");
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < ticketIds.length - 1;
  const totalTickets = ticketIds.length;

  // Navigate back and restore scroll position + expanded order
  const handleClose = useCallback(() => {
    haptic("light");
    // Save scroll position and expanded order ID for reliable restoration
    sessionStorage.setItem(SCROLL_STORAGE_KEY, String(savedScrollY));
    if (orderId) {
      sessionStorage.setItem(EXPANDED_ORDER_STORAGE_KEY, orderId);
    }
    navigate(returnPath, { replace: true });
  }, [navigate, returnPath, savedScrollY, orderId]);

  // Navigate to previous ticket
  const handlePrev = useCallback(() => {
    if (!hasPrev) return;
    haptic("light");
    const prevId = ticketIds[currentIndex - 1];
    navigate(`/admin/tickets/${prevId}/view`, {
      state: { from: returnPath, ticketIds, orderId, scrollY: savedScrollY },
      replace: true,
    });
  }, [
    hasPrev,
    ticketIds,
    currentIndex,
    navigate,
    returnPath,
    orderId,
    savedScrollY,
  ]);

  // Navigate to next ticket
  const handleNext = useCallback(() => {
    if (!hasNext) return;
    haptic("light");
    const nextId = ticketIds[currentIndex + 1];
    navigate(`/admin/tickets/${nextId}/view`, {
      state: { from: returnPath, ticketIds, orderId, scrollY: savedScrollY },
      replace: true,
    });
  }, [
    hasNext,
    ticketIds,
    currentIndex,
    navigate,
    returnPath,
    orderId,
    savedScrollY,
  ]);

  // Toggle toolbar on tap
  const handleImageTap = useCallback(() => {
    if (showMoreMenu || showRegenerateSheet) {
      setShowMoreMenu(false);
      setShowRegenerateSheet(false);
      return;
    }
    haptic("light");
    setShowToolbar((prev) => !prev);
  }, [showMoreMenu, showRegenerateSheet]);

  // Setup BackButton
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    tg.BackButton.onClick(handleClose);

    return () => {
      tg.BackButton.offClick(handleClose);
      tg.BackButton.hide();
    };
  }, [tg, handleClose]);

  // Load ticket image and detect orientation
  useEffect(() => {
    if (!ticketId) return;

    // AbortController for fetch cancellation on unmount
    const abortController = new AbortController();
    let isCancelled = false;

    setIsLoading(true);
    setError(null);
    setOrientation(null);
    setImageUrl(null);

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    const url = `${config.apiUrl}/admin/ticket/${ticketId}/image`;
    fetch(url, { headers: getAuthHeaders(), signal: abortController.signal })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) throw new Error("Изображение не найдено");
          if (res.status === 401) throw new Error("Нет доступа");
          throw new Error("Ошибка загрузки");
        }
        return res.blob();
      })
      .then((blob) => {
        // Guard against state updates after unmount
        if (isCancelled) return;

        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;

        const img = new Image();
        img.onload = () => {
          if (isCancelled) return;
          const aspectRatio = img.width / img.height;
          if (aspectRatio > 1.1) {
            setOrientation("landscape");
          } else if (aspectRatio < 0.9) {
            setOrientation("portrait");
          } else {
            setOrientation("square");
          }
          setImageUrl(objectUrl);
          setIsLoading(false);
        };
        img.onerror = () => {
          if (isCancelled) return;
          setError("Ошибка загрузки изображения");
          setIsLoading(false);
        };
        img.src = objectUrl;
      })
      .catch((err) => {
        // Ignore abort errors (expected on unmount)
        if (err instanceof Error && err.name === "AbortError") return;
        if (isCancelled) return;
        
        setError(getErrorMessage(err, "Ошибка загрузки"));
        setIsLoading(false);
      });

    return () => {
      isCancelled = true;
      abortController.abort();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [ticketId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showMoreMenu) {
          setShowMoreMenu(false);
        } else if (showRegenerateSheet) {
          setShowRegenerateSheet(false);
        } else {
          handleClose();
        }
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose, handlePrev, handleNext, showMoreMenu, showRegenerateSheet]);

  // Swipe detection for mobile
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (showMoreMenu || showRegenerateSheet) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (showMoreMenu || showRegenerateSheet) return;
    if (touchStartX.current === null || touchStartY.current === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;
    const threshold = 50;

    // Only horizontal swipe if horizontal movement > vertical
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
      if (diffX > 0 && hasNext) {
        handleNext();
      } else if (diffX < 0 && hasPrev) {
        handlePrev();
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // Reload image helper with proper loading state
  // Returns true if image was successfully loaded
  const reloadImage = useCallback(async (): Promise<boolean> => {
    if (!ticketId) return false;

    // Small delay to ensure server has finished generating the image
    await new Promise((r) => setTimeout(r, 500));

    const url = `${config.apiUrl}/admin/ticket/${ticketId}/image?t=${Date.now()}`;
    try {
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        adminLog.error("Failed to fetch ticket image:", res.status);
        return false;
      }

      const blob = await res.blob();

      // Create new object URL
      const newObjectUrl = URL.createObjectURL(blob);

      // Preload image before showing
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Revoke old URL only after new image is loaded
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
          }
          objectUrlRef.current = newObjectUrl;

          // Update orientation if needed
          const aspectRatio = img.width / img.height;
          if (aspectRatio > 1.1) {
            setOrientation("landscape");
          } else if (aspectRatio < 0.9) {
            setOrientation("portrait");
          } else {
            setOrientation("square");
          }

          // Clear error state and update image
          setError(null);
          setIsLoading(false);
          setImageUrl(newObjectUrl);
          resolve();
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = newObjectUrl;
      });

      return true;
    } catch (err) {
      adminLog.error("Failed to reload ticket image:", err);
      return false;
    }
  }, [ticketId]);

  // Open send sheet
  const openSendSheet = useCallback((target: "single" | "all") => {
    setShowMoreMenu(false);
    setSendTarget(target);
    setShowSendSheet(true);
    haptic("light");
  }, []);

  // Execute send with optional custom message
  const executeSend = useCallback(
    async (customMessage?: string) => {
      if (isProcessing) return;

      setShowSendSheet(false);
      setProcessingAction(sendTarget === "all" ? "resendAll" : "resend");

      try {
        const options = customMessage ? { customMessage } : undefined;

        if (sendTarget === "all") {
          if (!orderId) return;
          const result = await resendTickets(orderId, options);
          if (result) {
            haptic("success");
            safeShowAlert(result.message ?? "Билеты отправлены");
          } else {
            haptic("error");
          }
        } else {
          if (!ticketId) return;
          const result = await resendTicket(ticketId, options);
          if (result) {
            haptic("success");
            safeShowAlert(result.message ?? "Билет отправлен");
          } else {
            haptic("error");
          }
        }
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка отправки"));
      } finally {
        setProcessingAction(null);
      }
    },
    [ticketId, orderId, sendTarget, isProcessing, resendTicket, resendTickets],
  );

  // Open regenerate sheet for single ticket
  const openRegenerateSheet = useCallback((target: RegenerateTarget) => {
    setShowMoreMenu(false);
    setRegenerateTarget(target);
    setShowRegenerateSheet(true);
    haptic("light");
  }, []);

  // Execute regeneration (without sending)
  const executeRegenerate = useCallback(
    async (options: { imageOnly: boolean }) => {
      if (isProcessing || isRegenerating) return;

      setShowRegenerateSheet(false);
      haptic("light");

      let success = false;

      try {
        if (regenerateTarget === "single") {
          if (!ticketId) {
            return;
          }

          const result = await regenerateTicket(ticketId, {
            imageOnly: options.imageOnly,
            sendToUser: false, // Don't send, just regenerate
          });

          if (result) {
            success = true;
            haptic("success");
            safeShowAlert(result.message ?? "Билет перегенерирован");
          } else {
            haptic("error");
            return;
          }
        } else {
          if (!orderId) {
            return;
          }

          const result = await regenerateTickets(orderId, {
            imageOnly: options.imageOnly,
            sendToUser: false, // Don't send, just regenerate
          });

          if (result) {
            success = true;
            haptic("success");
            safeShowAlert(result.message ?? "Билеты перегенерированы");
          } else {
            haptic("error");
            return;
          }
        }

        // Reload image after successful regeneration
        if (success) {
          const imageLoaded = await reloadImage();
          if (!imageLoaded) {
            // If image failed to load, show error but keep old image
            adminLog.warn("Image reload failed, keeping old image");
          }
        }
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка перегенерации"));
        return; // Don't open send sheet on error
      }

      // After successful regeneration, offer to send
      if (success) {
        // Small delay for better UX
        safeSetTimeout(() => {
          openSendSheet(regenerateTarget === "single" ? "single" : "all");
        }, 300);
      }
    },
    [
      ticketId,
      orderId,
      regenerateTarget,
      isProcessing,
      isRegenerating,
      reloadImage,
      openSendSheet,
      regenerateTicket,
      regenerateTickets,
    ],
  );

  const isPortrait = orientation === "portrait" || orientation === "square";

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      {/* Top header bar */}
      <div
        className={`absolute top-0 left-0 right-0 z-20 transition-all duration-200 ${
          showToolbar && !isRegenerating
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-full pointer-events-none"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Close button */}
            <button
              type="button"
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white active:bg-white/20"
            >
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M19 12H5M12 19L5 12L12 5" />
              </svg>
            </button>

            {/* Ticket counter */}
            {totalTickets > 1 && (
              <div className="text-white/90 text-[14px] font-medium">
                {currentIndex + 1} из {totalTickets}
              </div>
            )}

            {/* Spacer for symmetry */}
            <div className="w-10" />
          </div>
        </div>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 relative overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Image content */}
        {imageUrl &&
          !isLoading &&
          !error &&
          (isPortrait ? (
            <div
              className="absolute inset-0 overflow-y-auto overscroll-contain bg-slate-100"
              onClick={handleImageTap}
            >
              <img
                src={imageUrl}
                alt="Билет"
                className="w-full h-auto select-none"
                draggable={false}
              />
            </div>
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black"
              onClick={handleImageTap}
            >
              <img
                src={imageUrl}
                alt="Билет"
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            </div>
          ))}

        {/* Processing overlay - fullscreen with pulsing heart for resend/regenerate */}
        {(isRegenerating ||
          processingAction === "resend" ||
          processingAction === "resendAll") && (
          <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center">
            <div className="text-center">
              <Loader className="mx-auto mb-4" />
              <p className="text-white text-[16px] font-medium">
                {isRegenerating ? "Перегенерация..." : "Отправка..."}
              </p>
              <p className="text-white/60 text-[13px] mt-1">
                {isRegenerating
                  ? "Создание изображения билета"
                  : processingAction === "resendAll"
                    ? "Отправка всех билетов"
                    : "Отправка билета пользователю"}
              </p>
            </div>
          </div>
        )}

        {/* Loading state - pulsing heart with semi-transparent bg */}
        {isLoading && !isRegenerating && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center">
              <Loader className="mx-auto mb-3" />
              <p className="text-white/60 text-[14px]">Загрузка...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900 px-8">
            <div className="text-center">
              <IconAlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <p className="text-white text-[16px] font-medium mb-2">{error}</p>
              <p className="text-white/60 text-[14px] mb-6">
                Попробуйте перегенерировать билет
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-2.5 bg-white/10 text-white rounded-xl font-medium text-[14px] active:bg-white/20"
                >
                  Назад
                </button>
                <button
                  type="button"
                  onClick={() => openRegenerateSheet("single")}
                  disabled={isProcessing || isRegenerating}
                  className="px-5 py-2.5 bg-amber-500 text-white rounded-xl font-medium text-[14px] active:bg-amber-600 disabled:opacity-50"
                >
                  Перегенерировать
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation arrows for multiple tickets */}
        {totalTickets > 1 && !isLoading && !error && !isRegenerating && (
          <>
            {hasPrev && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full transition-opacity duration-200 ${
                  showToolbar ? "opacity-100" : "opacity-0 pointer-events-none"
                } ${
                  isPortrait
                    ? "bg-black/30 text-white shadow-lg active:bg-black/50"
                    : "bg-white/10 text-white active:bg-white/20"
                }`}
              >
                <IconChevronLeft className="w-7 h-7" />
              </button>
            )}

            {hasNext && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full transition-opacity duration-200 ${
                  showToolbar ? "opacity-100" : "opacity-0 pointer-events-none"
                } ${
                  isPortrait
                    ? "bg-black/30 text-white shadow-lg active:bg-black/50"
                    : "bg-white/10 text-white active:bg-white/20"
                }`}
              >
                <IconChevronRight className="w-7 h-7" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Bottom toolbar */}
      <div
        className={`relative z-20 transition-all duration-200 ${
          showToolbar && !isRegenerating
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-full pointer-events-none"
        }`}
      >
        <div className="bg-gradient-to-t from-black/80 to-black/60 backdrop-blur-sm">
          <div
            className="flex items-center justify-around px-4 py-3"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            }}
          >
            {/* Send button */}
            <button
              type="button"
              onClick={() => openSendSheet("single")}
              disabled={isProcessing || isRegenerating}
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl active:bg-white/10 disabled:opacity-50 min-w-[80px]"
            >
              <IconSend className="w-6 h-6 text-white" />
              <span className="text-[11px] text-white/80 font-medium">
                Отправить
              </span>
            </button>

            {/* Regenerate button */}
            <button
              type="button"
              onClick={() => openRegenerateSheet("single")}
              disabled={isProcessing || isRegenerating}
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl active:bg-white/10 disabled:opacity-50 min-w-[80px]"
            >
              <IconRefreshCw className="w-6 h-6 text-white" />
              <span className="text-[11px] text-white/80 font-medium">
                Перегенерировать
              </span>
            </button>

            {/* More actions button (if multiple tickets) */}
            {orderId && totalTickets > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  haptic("light");
                  setShowMoreMenu(true);
                }}
                disabled={isProcessing || isRegenerating}
                className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl active:bg-white/10 disabled:opacity-50 min-w-[80px]"
              >
                <IconMoreVertical className="w-6 h-6 text-white" />
                <span className="text-[11px] text-white/80 font-medium">
                  Ещё
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* More menu overlay */}
      {showMoreMenu && (
        <div
          className="absolute inset-0 z-30 bg-black/60"
          onClick={() => setShowMoreMenu(false)}
        />
      )}

      {/* More menu bottom sheet */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-40 bg-slate-900 rounded-t-2xl transition-transform duration-300 ${
          showMoreMenu ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>

        {/* Menu content */}
        <div className="px-4 pb-4">
          <h3 className="text-[13px] font-medium text-white/50 uppercase tracking-wide mb-3 px-1">
            Все билеты заказа ({totalTickets})
          </h3>

          <div className="space-y-1">
            <button
              type="button"
              onClick={() => openSendSheet("all")}
              disabled={isProcessing || isRegenerating}
              className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl bg-white/5 text-white active:bg-white/10 disabled:opacity-50"
            >
              <IconSend className="w-6 h-6 text-blue-400" />
              <div className="text-left">
                <div className="text-[15px] font-medium">Отправить все</div>
                <div className="text-[12px] text-white/50">
                  Повторная отправка всех билетов
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => openRegenerateSheet("all")}
              disabled={isProcessing || isRegenerating}
              className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl bg-white/5 text-white active:bg-white/10 disabled:opacity-50"
            >
              <IconRefreshCw className="w-6 h-6 text-amber-400" />
              <div className="text-left">
                <div className="text-[15px] font-medium">
                  Перегенерировать все
                </div>
                <div className="text-[12px] text-white/50">
                  Создать новые изображения
                </div>
              </div>
            </button>
          </div>

          {/* Cancel button */}
          <button
            type="button"
            onClick={() => setShowMoreMenu(false)}
            className="w-full mt-4 py-3.5 rounded-xl bg-white/10 text-white font-medium text-[15px] active:bg-white/15"
          >
            Отмена
          </button>
        </div>
      </div>

      {/* Regenerate sheet - shared component with dark variant */}
      <RegenerateSheet
        isOpen={showRegenerateSheet}
        onClose={() => setShowRegenerateSheet(false)}
        onConfirm={(options) =>
          executeRegenerate({ imageOnly: options.imageOnly })
        }
        isProcessing={isRegenerating}
        variant="dark"
      />

      {/* Send sheet - shared component with dark variant */}
      <SendSheet
        isOpen={showSendSheet}
        ticketCount={sendTarget === "all" ? totalTickets : 1}
        onClose={() => setShowSendSheet(false)}
        onConfirm={executeSend}
        isProcessing={isProcessing}
        variant="dark"
      />
    </div>
  );
}
